import React from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { MARKETPLACE_ROUTE } from "constants/route-constants";
import { MarketPlaceType } from "@vahak/core/dist/constants";
import { MARKETPLACE_ISR_REVALIDATE_SECONDS } from "../../constants/marketplace.constants";
import { getRouteLocalizationNamespaces } from "../../methods/getRouteLocalizationNamespaces";
import { MarketPlacePageProps } from "../../components/marketplace-new/marketplacePageHelpers";
import MarketplaceV3 from "../../components/service-pages/marketplace/marketplace-v3/MarketplaceV3";
import CityLandingPage from "../../components/city/CityLandingPage";
import { parseMarketplaceLocationFromParamSegment } from "../../components/service-pages/marketplace/marketplace-v3/marketplaceV3.constants";

const { i18n } = require("../../next-i18next.config");

const MARKETPLACE_SEGMENTS = [MarketPlaceType.LOAD, MarketPlaceType.LORRY] as const;

function normalizeParamArray(params: string | string[] | undefined) {
    if (!params) {
        return undefined;
    }

    return Array.isArray(params) ? params : [params];
}

function buildMarketplaceStaticPaths() {
    const locales: string[] = i18n.locales;
    const paths: { params: { params: string[] }; locale: string }[] = [];

    for (const locale of locales) {
        for (const segment of MARKETPLACE_SEGMENTS) {
            paths.push({ params: { params: [segment] }, locale });
        }
    }

    return paths;
}

const MarketplacePage = (props: MarketPlacePageProps) => {
    if (props.marketplaceType === MarketPlaceType.LORRY) {
        return <CityLandingPage marketplaceLorry={{ urlSrc: props.urlSrc, urlDest: props.urlDest }} />;
    }

    return <MarketplaceV3 {...props} />;
};

export default MarketplacePage;

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: buildMarketplaceStaticPaths(),
        fallback: "blocking"
    };
};

export const getStaticProps: GetStaticProps<MarketPlacePageProps> = async (context) => {
    const paramArray = normalizeParamArray(context.params?.params as string | string[] | undefined);
    const marketplaceSegment = paramArray?.[0];

    if (marketplaceSegment !== MarketPlaceType.LOAD && marketplaceSegment !== MarketPlaceType.LORRY) {
        return { notFound: true };
    }

    if ((paramArray?.length ?? 0) >= 3) {
        return { notFound: true };
    }

    let srcParam = "";
    let destParam = "";
    const locationSegment = paramArray?.[1];

    if (locationSegment) {
        const { src, dest } = parseMarketplaceLocationFromParamSegment(locationSegment);
        srcParam = src;
        destParam = dest;
    }

    return {
        props: {
            ...(await serverSideTranslations(context.locale!, getRouteLocalizationNamespaces(MARKETPLACE_ROUTE))),
            marketplaceType: marketplaceSegment,
            urlPage: null,
            urlSrc: srcParam,
            urlDest: destParam,
            urlFilters: null,
            data: null,
            route: MARKETPLACE_ROUTE,
            embeddedWithProfile: false
        },
        revalidate: MARKETPLACE_ISR_REVALIDATE_SECONDS
    };
};
